import type { Hit, Pointer } from './platform';

export interface ScrollRect { x:number;y:number;w:number;h:number }
/** Native-canvas list gestures. Activate cards on release only, after excluding a drag. */
export class StudioScroll {
  offset=0;
  loaded=6;
  loadingAt=0;
  revealAt=0;
  revealFrom=0;
  private key='';
  private max=0;
  private velocity=0;
  private lastFrame=0;
  private drag?:{id:number;start:number;startX:number;last:number;time:number;moved:boolean;action?:()=>void};
  private ignored=new Set<number>();
  rect:ScrollRect={x:0,y:0,w:0,h:0};
  reset(){this.key='';this.cancel();}
  cancel(){if(this.drag)this.ignored.add(this.drag.id);this.drag=undefined;this.velocity=0;}
  frame(key:string,rect:ScrollRect,total:number,rowHeight:number,now=Date.now(),columns=2,batch=6){
    if(this.key!==key){this.cancel();this.key=key;this.offset=0;this.loaded=Math.min(total,batch);this.loadingAt=0;this.revealAt=0;}
    this.rect=rect;this.loaded=Math.min(this.loaded,total);
    if(this.loadingAt&&now-this.loadingAt>=240){this.revealFrom=this.loaded;this.revealAt=now;this.loaded=Math.min(total,this.loaded+6);this.loadingAt=0;}
    this.max=Math.max(0,Math.ceil(this.loaded/columns)*rowHeight+(batch===6?50:0)-rect.h);
    const dt=Math.min(40,Math.max(0,now-this.lastFrame));this.lastFrame=now;
    if(!this.drag&&Math.abs(this.velocity)>.02){this.offset+=this.velocity*dt;this.velocity*=Math.exp(-dt/170);}
    const clamped=Math.max(0,Math.min(this.max,this.offset));if(clamped!==this.offset)this.velocity=0;this.offset=clamped;
    // The catalog is local; stagger preview preparation with a short, non-blocking reveal.
    if(total>this.loaded&&!this.loadingAt&&this.offset>=Math.max(0,this.max-100))this.loadingAt=now;
  }
  wheel(delta:number){this.velocity=0;this.offset=Math.max(0,Math.min(this.max,this.offset+delta));}
  pointer(kind:'down'|'move'|'up'|'cancel',point:Pointer,hits:Hit[]){
    if(kind==='down')this.ignored.delete(point.id);
    if(this.ignored.has(point.id)){if(kind==='up'||kind==='cancel')this.ignored.delete(point.id);return true;}
    if(kind==='down'){
      if(this.drag){this.ignored.add(point.id);return true;}
      const r=this.rect;if(point.x<r.x||point.x>r.x+r.w||point.y<r.y||point.y>r.y+r.h)return false;
      const hit=[...hits].reverse().find(v=>!v.disabled&&point.x>=v.x&&point.x<=v.x+v.w&&point.y>=v.y&&point.y<=v.y+v.h);
      const moving=Math.abs(this.velocity)>.1;this.velocity=0;
      this.drag={id:point.id,start:point.y,startX:point.x,last:point.y,time:Date.now(),moved:moving,action:hit?.action};return true;
    }
    const d=this.drag;if(!d||d.id!==point.id)return false;
    if(Math.hypot(point.x-d.startX,point.y-d.start)>7)d.moved=true;
    if(kind==='move'){
      const now=Date.now(),dy=d.last-point.y;
      if(d.moved){this.offset=Math.max(0,Math.min(this.max,this.offset+dy));this.velocity=Math.max(-3,Math.min(3,dy/Math.max(8,now-d.time)));}
      d.last=point.y;d.time=now;
    }else {
      this.drag=undefined;
      if(kind==='cancel')this.velocity=0;
      else if(!d.moved){this.velocity=0;d.action?.();}
      else if(Date.now()-d.time>100)this.velocity=0;
    }
    return true;
  }
  /** Keep DOM hit targets within the clipped viewport; all carry the same drag/wheel behavior. */
  clipHits(hits:Hit[],start:number,minTouch:number){
    const r=this.rect,children=hits.splice(start);
    for(const hit of children){const y=Math.max(r.y,hit.y),end=Math.min(r.y+r.h,hit.y+hit.h);if(end-y<minTouch)continue;
      hits.push({...hit,y,h:end-y,drag:true,onScroll:delta=>this.wheel(delta),onAdjust:direction=>this.wheel(direction*80)});
    }
  }
}
